<template>
  <v-container>
    <v-row>
      <v-col class="checklist-left-col" cols="3">
        <v-card class="mb-2" elevation="2">
          <TitleWithAction
            :loading="false"
            :subtitle="versionSelected ? getActiveStatusDuration(versionSelected.status):'Sin versión'"
            :title=" versionSelected ? '['+processInfo.code+'] V'+versionSelected.version + ': ' + getStatusInfo(versionSelected.status).name :'-'"
          >
            <template v-slot:action>
              <ChecklistVersionSelector
                :show-add-button="showAddVersionButton"
                :version-selected="versionSelected"
                :versions="versions"
                @version-selected="selectVersion"
                @create-new-version="createNewVersion"
              />
            </template>
          </TitleWithAction>
          <ChecklistVersionActions
            v-if="versionSelected &&versionSelected.status !== 'approved' && versionSelected.status !== 'completed'"
            :checklist-saving="checklistSaving"
            :has-pending-sections="hasPendingSections"
            :reject-status-reason.sync="rejectStatusReason"
            :version-selected="versionSelected"
            :version-selected-data="versionSelectedData"
            @save-checklist="saveChecklistData"
            @update-version-status="updateVersionStatus"
            @get-next-status-info="getNextStatusInfo"
            @get-previous-status-info="getPreviousStatusInfo"
          />
        </v-card>
        <ChecklistSectionsIndex
          v-if="versionSelected && versionSelectedData.metadata"
          :expanded-panels.sync="expandedPanels"
          :process-info="processInfo"
          :sections="getSectionsIndex()"
          @scroll-to-parameter="scrollToParameter"
        />
      </v-col>
      <v-col class="checklist-right-col" cols="9">
        <PMChecklistForm
          v-if="versionSelectedData && versionSelected && versionSelectedData.metadata"
          ref="checklistForm"
          :data="versionSelectedData"
          :from-view="fromView"
          :process-info="processInfo"
          :version-status="versionSelected ? versionSelected.status : 'draft'"
          @checklist-data-updated="onChecklistDataUpdated"
        />
      </v-col>
    </v-row>
    <MessageOverlayDialog
      :actionActiveText="actionActiveText"
      :showActionTextDialog="showActionTextDialog"
    />
  </v-container>
</template>

<script>
  import { getResources, resourceAction } from '@/services/service_resource_manager'
  import model_checklist from '@/constants/models/pkc/model_checklist'
  import model_process from '@/constants/models/model_process'
  import vuex_authentication from '@/store/modules/vuex_authentication'
  import helpers, { decodeBase64WithTildes } from '@/plugins/helpers'
  import TitleWithAction from '@/components/global_app/TitleWithAction.vue'
  import MessageOverlayDialog from '@/components/global_app/MessageOverlayDialog.vue'
  import moment from 'moment'
  import PMChecklistForm from '@/components/management/processManagement/components/PMChecklistForm.vue'
  import ChecklistVersionSelector
    from '@/components/management/processManagement/components/checklist/ChecklistVersionSelector.vue'
  import ChecklistVersionActions
    from '@/components/management/processManagement/components/checklist/ChecklistVersionActions.vue'
  import ChecklistSectionsIndex
    from '@/components/management/processManagement/components/checklist/ChecklistSectionsIndex.vue'
  import service_management from '@/services/service_management'
  import model_pkcProcess from '@/constants/models/pkc/model_pkcProcess'

  export default {
    name: 'ProcessManagementChecklist',
    components: {
      PMChecklistForm,
      MessageOverlayDialog,
      TitleWithAction,
      ChecklistVersionSelector,
      ChecklistVersionActions,
      ChecklistSectionsIndex
    },
    props: {
      processInfo: Object,
      fromView: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        helpers,
        modelData: model_checklist,
        expandedPanels: [],
        rejectStatusReason: '',
        processPath: `${this.processInfo[model_process.MASTER_ACCOUNT_CODE]}/${this.processInfo[model_process.SUB_ACCOUNT_CODE]}/${this.processInfo[model_process.PROJECT_CODE]}/${this.processInfo[model_process.CODE]}`,
        actionActiveText: null,
        loadingActions: false,
        versions: [],
        versionNumberSelected: 0,
        versionSelected: null,
        versionSelectedData: {
          metadata: null,
          resources: []
        },
        lastVersion: null,
        checklistSaving: false,
        checklistData: null,
        versionMenuOpen: false
      }
    },
    computed: {
      model_pkcProcess() {
        return model_pkcProcess
      },
      folderPath() {
        return `${this.processPath}/DOCUMENTS/${this.modelData.GLOBAL_FOLDER_NAME}/${this.modelData.FOLDER_NAME}_v${this.versionNumberSelected}`
      },
      showActionTextDialog() {
        return this.actionActiveText !== null
      },
      hasPendingSections() {
        if (!this.versionSelectedData.metadata?.values?.sections) {
          return false
        }

        if (this.versionSelectedData.metadata && this.versionSelectedData.metadata.values && this.versionSelectedData.metadata.values.sections) {
          const sections = this.versionSelectedData.metadata.values.sections
          for (const sectionKey in sections) {
            const section = sections[sectionKey]
            if (!section.value) section.value = 'pending'
            if (section && section.parameters) {
              for (const paramKey in section.parameters) {
                const param = section.parameters[paramKey]
                if (!param.value) param.value = 'pending'
                if (!param.documentationLink) param.documentationLink = ''
                if (!param.extraInfo) param.extraInfo = null
                if (!param.customCheckMethod) param.customCheckMethod = ''
                if (!param.customCheckMethodValidatedData) param.customCheckMethodValidatedData = null
                if (!param.timestamp) param.timestamp = null
                if (!param.comment) param.comment = ''
              }
            }
          }
        }

        const sections = this.versionSelectedData.metadata.values.sections
        for (const sectionKey in sections) {
          const section = sections[sectionKey]
          if (section && section.parameters && section.value !== 'not_required') {
            for (const paramKey in section.parameters) {
              const param = section.parameters[paramKey]
              if (param.value === 'pending') {
                return true
              }
            }
          }
        }
        return false
      },
      showAddVersionButton() {
        let showButton = false
        if (this.versions.length === 0) {
          showButton = true
        } else if (this.versions.find(v => v.status !== 'approved' && v.status !== 'completed') == null) {
          showButton = true
        }
        return showButton
      }
    },
    async created() {
      await this.checkPath()
      await this.getVersions()
    },
    watch: {
      versionNumberSelected(val) {
        const currentVersion = this.$route.query.version
        if (currentVersion != val) {
          this.safeRouterPush({
            query: {
              ...this.$route.query,
              version: val
            }
          })
        }
      },
      versionSelectedData: {
        handler() {
          this.updateExpandedPanels()
        },
        deep: true
      }
    },
    methods: {
      safeRouterPush(location) {
        return this.$router.push(location).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            throw err
          }
        })
      },

      versionText: item => 'Versión ' + item.version + ' | ' + moment.unix(item.creation_timestamp).fromNow(),

      async checkPath() {
        await this.updateActionText('Comprobando directorio de documentos', false)
        const resourcesFound = await getResources(this.processPath + '/DOCUMENTS')
        if (!resourcesFound.data.extra.resources || !resourcesFound.data.extra.resources.find(r => r.name === this.modelData.GLOBAL_FOLDER_NAME)) {
          await this.updateActionText('Detectado primer acceso. Creando recursos necesarios')
          await service_management.initializeDirectory(this.processPath)
        }
        await this.updateActionText(null, false)
      },

      async getVersions() {

        this.versions = await this.modelData.getVersions(this.processInfo[model_process.CODE])

        this.lastVersion = this.versions.length > 0 ? this.versions[0].version : 0
        const routeVersion = this.$route.query['version']
        if (routeVersion) {
          await this.selectVersion(this.versions.find(v => v.version == routeVersion))
        } else if (this.versions.length > 0 && this.versionNumberSelected === 0) {
          await this.selectVersion(this.versions[0])
        }
      },

      async selectVersion(version, resources = true) {
        if (version) {
          this.versionNumberSelected = version.version
          this.versionSelected = version
          this.versionSelectedData.metadata = null
          this.versionSelectedData.resources = []
          await this.getVersionData(resources)
        }
      },

      async getVersionData(resources = true) {
        await this.updateActionText('Cargando información de la versión')
        const metadata = await resourceAction('download_file', `${this.folderPath}/${this.modelData.FOLDER_NAME}_METADATA.json`)
        if (!metadata.data.filecontent) {
          return
        }
        this.versionSelectedData.metadata = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(metadata.data.filecontent), c => c.charCodeAt(0))))

        if (resources) {
          await this.getResources()
        }
        await this.updateActionText(null)
      },

      async createNewVersion() {
        this.versionSelectedData = {
          metadata: null,
          resources: []
        }
        this.versionNumberSelected = parseInt(this.lastVersion) + 1
        await this.updateActionText('Creando nueva versión')
        await this.modelData.createModelVersion(this.processInfo.code)
        await this.getVersions(false, false)
        await this.selectVersion(this.versions.find(v => v.version === this.versionNumberSelected))
        await this.updateActionText('Creando estructura de carpetas y ficheros', false)
        await resourceAction('create_folder', `${this.folderPath}`)
        await resourceAction('create_folder', `${this.folderPath}/RESOURCES`)

        let response = await resourceAction('download_file', `//RESOURCES/pkc/checklist_data.json`)
        const newVersionMetadata = JSON.parse(decodeBase64WithTildes(response.data.filecontent))
        console.log('NVM', newVersionMetadata)
        newVersionMetadata.info.version = this.versionNumberSelected
        this.versionSelectedData.metadata = newVersionMetadata
        await this.addStatusChangeToMetadata('draft')
        await this.updateMetadataFile(this.versionSelectedData.metadata)
        await this.updateActionText(null, false)
      },

      async updateMetadataFile(metadata) {
        const content = JSON.stringify(metadata)
        const file = new File([content], `${this.modelData.FOLDER_NAME}_METADATA.json`, { type: 'application/json' })
        await resourceAction('upload_files', this.folderPath, [file])
      },

      async getResources() {
        await this.updateActionText('Cargando recursos de la versión')
        const resources = await getResources(`${this.folderPath}/RESOURCES`)
        this.versionSelectedData.resources = resources.data.extra.resources || []
        await this.updateActionText(null, false)
      },

      async addStatusChangeToMetadata(statusId) {
        this.versionSelected.status = statusId
        const statusExists = this.versionSelectedData.metadata.info.statuses && this.versionSelectedData.metadata.info.statuses[statusId] != null
        this.versionSelectedData.metadata.info.statuses[statusId] = {
          user: this.$store.state[vuex_authentication.constants.NAMESPACE][vuex_authentication.constants.S_USERNAME],
          startTimestamp: statusExists ? this.versionSelectedData.metadata.info.statuses[statusId].startTimestamp : new moment().unix(),
          finishTimestamp: null,
          rejectReason: this.rejectStatusReason ? this.rejectStatusReason : null
        }
        this.rejectStatusReason = ''
        if (this.getPreviousStatusInfo(statusId)) {
          this.versionSelectedData.metadata.info.statuses[this.getPreviousStatusInfo(statusId).id].finishTimestamp = new moment().unix()
        }
        if (this.getNextStatusInfo(statusId)) {
          delete this.versionSelectedData.metadata.info.statuses[this.getNextStatusInfo(statusId).id]
        }
      },

      async updateVersionStatus(statusId) {
        await this.updateActionText('Actualizando estado de la versión')
        await this.modelData.updateStatus(this.versionSelected.id, statusId)
        await this.addStatusChangeToMetadata(statusId)
        await this.updateMetadataFile(this.versionSelectedData.metadata)
        await this.getVersions()
        await this.getVersionData(false)
      },

      getStatusInfo(status) {
        return this.modelData.statuses.find(s => s.id === status)
      },

      getNextStatusInfo(status) {
        const index = this.modelData.statuses.findIndex(s => s.id === status)
        return this.modelData.statuses[index + 1]
      },

      getPreviousStatusInfo(status) {
        const index = this.modelData.statuses.findIndex(s => s.id === status)
        return this.modelData.statuses[index - 1]
      },

      async updateActionText(text, wait = true) {
        this.actionActiveText = text
        await new Promise(resolve => setTimeout(resolve, wait ? 500 : 0))
      },

      getSectionsIndex() {
        if (!this.versionSelectedData.metadata?.values?.sections) {
          return []
        }

        const sections = []

        Object.entries(this.versionSelectedData.metadata.values.sections).forEach(([sectionKey, section]) => {
          if (section && section.parameters) {
            const total = Object.keys(section.parameters).length
            const completed = Object.values(section.parameters).filter(param =>
              param.value === 'checked' || param.value === 'not_required'
            ).length

            const isDisabled = section.value === 'not_required'
            const isCompleted = isDisabled || completed === total

            sections.push({
              key: sectionKey,
              name: section.name,
              icon: section.icon,
              total,
              completed,
              isCompleted,
              isDisabled,
              parameters: section.parameters,
              value: section.value
            })
          }
        })

        return sections
      },

      updateExpandedPanels() {
        this.$nextTick(() => {
          const sections = this.getSectionsIndex()
          const firstIncompleteIndex = sections.findIndex(section => !section.isCompleted)

          if (firstIncompleteIndex !== -1) {
            this.expandedPanels = [firstIncompleteIndex]
          }
        })
      },

      scrollToSection(sectionKey) {
        this.$nextTick(() => {
          const element = document.querySelector(`[data-section="${sectionKey}"]`)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      },

      scrollToParameter(sectionKey, paramKey) {
        if (this.$refs.checklistForm) {
          this.$nextTick(() => {
            this.$refs.checklistForm.scrollToParameter(sectionKey, paramKey)
          })
        }
      },

      async saveChecklistData() {
        try {
          this.checklistSaving = true
          await this.updateActionText('Guardando checklist...')

          if (this.$refs.checklistForm) {
            await this.$refs.checklistForm.saveChecklist()
            helpers.setAlertMessage(0, 'Checklist guardado correctamente')
          }

        } catch (error) {
          console.error('Error al guardar el checklist:', error)
          helpers.setAlertMessage(1, 'Error al guardar el checklist')
        } finally {
          this.checklistSaving = false
          await this.updateActionText(null, false)
        }
      },

      getActiveStatusName(statusId) {
        const status = this.modelData.statuses.find(s => s.id === statusId)
        return status ? status.name : 'Estado desconocido'
      },

      getStatusIcon(statusId) {
        const statusIcons = {
          'draft': 'edit',
          'pending': 'hourglass_empty',
          'in_review': 'rate_review',
          'approved': 'check_circle',
          'completed': 'done_all',
          'rejected': 'cancel'
        }
        return statusIcons[statusId] || 'help_outline'
      },

      getActiveStatusDuration(statusId) {
        if (!this.versionSelectedData.metadata?.info?.statuses?.[statusId]) {
          return 'Tiempo no disponible'
        }

        const statusInfo = this.versionSelectedData.metadata.info.statuses[statusId]
        const startTime = statusInfo.startTimestamp

        if (!startTime) {
          return 'Tiempo no disponible'
        }

        if (statusInfo.finishTimestamp) {
          return helpers.getDifferenceBetweenTimestamps(startTime, statusInfo.finishTimestamp)
        }

        if (statusId === 'completed') return `Completado el ${moment.unix(startTime).format('DD/MM/YYYY HH:mm')} (${moment.unix(startTime).fromNow()})`
        return `Desde ${moment.unix(startTime).fromNow()}`
      },

      getStatusProgress() {
        const currentIndex = this.modelData.statuses.findIndex(s => s.id === this.versionSelected.status)
        const totalStates = this.modelData.statuses.length

        if (currentIndex === -1) return '0/0'

        return `${currentIndex + 1}/${totalStates}`
      },

      onChecklistDataUpdated(updatedData) {
        if (this.versionSelectedData.metadata && this.versionSelectedData.metadata.values) {
          this.versionSelectedData.metadata.values = { ...updatedData }
        }
      },

      isSectionEnabled(sectionKey) {
        if (!this.versionSelectedData.metadata?.values?.sections) {
          return false
        }

        const section = this.versionSelectedData.metadata.values.sections[sectionKey]
        return section && section.value !== 'not_required'
      }
    }
  }
</script>

<style lang="scss">
  @import "/src/assets/styles/resources/colors.scss";
  @import "/src/assets/styles/resources/variables.scss";

  .checklist-left-col,
  .checklist-right-col {
    font-family: 'Roboto', sans-serif !important;
    min-height: 0;
  }

  .select-item-list {
    max-width: 240px !important;

    .v-list-item {
      height: 32px;
    }

    height: 40px !important;

    button {
      height: 62px !important;
    }

    fieldset {
      height: 48px !important;
    }

    .v-select__selections {
      padding: 0px !important;
    }

    .v-select__slot {
      height: 40px !important;

      .v-input__append-inner {
        height: 100%;
        align-items: center;

        i {
          font-size: 26px !important;
        }
      }
    }
  }

  .stepper {
    display: flex;
    padding: 0;
    width: 60rem;
    list-style: none;
    position: relative;
    padding-left: 18px !important;

    &::before {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      content: "";
    }
  }

  .stepper__item {
    border-radius: 4px !important;
    border-bottom-left-radius: 0 !important;
    border-top-left-radius: 0 !important;
    flex: 100%;
    padding: 12px 12px 12px 32px;
    background: $color_app_contrast_medium_low;
    margin: 0 0 0 -17px;
    -webkit-clip-path: polygon(20px 50%, 0% 0%, calc(100% - 20px) 0%, 100% 50%, calc(100% - 20px) 100%, 0% 100%);
    box-shadow: inset 8px 0px 20px 0px #00000014;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    &.complete {
      background: rgb(80 240 255 / 20%);
      font-weight: bold;
    }

    &.active {
      background: $color_app_primary !important;
      font-weight: bold;
      color: white;
    }

    &:first-child {
      -webkit-clip-path: polygon(0% 0%, calc(100% - 20px) 0%, 100% 50%, calc(100% - 20px) 100%, 0% 100%);
      padding-left: 12px;
    }

    &:last-child {
      -webkit-clip-path: polygon(20px 50%, 0% 0%, 100% 0%, 100% 100%, 0% 100%);
    }
  }
</style>